import { useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronsUpDown, Globe, LogOut, User } from 'lucide-react'

import { Link } from '@/components/common/CustomLink'
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { menuConfig, defaultExpandedMenus } from '@/config/menuConfig'
import type { MenuItem } from '@/config/menuConfig'
import { LANGUAGE } from '@/i18n'
import { cn } from '@/lib/utils'

export default function Layout() {
  const { t } = useTranslation()
  const location = useLocation()

  const { user, logout } = useAuth()
  const { language } = useLanguage()
  const [expandedMenus, setExpandedMenus] =
    useState<string[]>(defaultExpandedMenus)

  const handleLogout = () => {
    logout()
  }

  const toggleMenu = (menuName: string) => {
    setExpandedMenus(prev =>
      prev.includes(menuName)
        ? prev.filter(name => name !== menuName)
        : [...prev, menuName]
    )
  }

  const isActive = (href: string) => {
    return location.pathname.includes(href)
  }

  const isParentActive = (item: MenuItem) => {
    if (item.href && isActive(item.href)) return true
    if (item.subItems) {
      return item.subItems.some(sub => isActive(sub.href))
    }
    return false
  }

  const isMenuExpanded = (menuName: string) => expandedMenus.includes(menuName)

  return (
    <div className='min-h-screen bg-background flex'>
      <aside className='w-64 border-r bg-card flex flex-col justify-between py-6'>
        <div>
          <div className='px-6'>
            <Link to='/' className='text-xl font-bold flex items-center gap-2'>
              <img
                src='/login-logo.png'
                alt='ST Engineering Logo'
                className='h-8 w-auto'
              />
            </Link>
          </div>

          <nav className='mt-8 px-4 space-y-4'>
            {menuConfig.map(item => {
              const Icon = item.icon
              const hasChildren = Boolean(item.subItems?.length)
              const itemName = t(item.translationKey)

              return (
                <div key={item.translationKey} className='space-y-2'>
                  {hasChildren ? (
                    <>
                      <button
                        onClick={() => toggleMenu(item.translationKey)}
                        className={cn(
                          'w-full flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-semibold text-[#181D27] transition-colors',
                          isParentActive(item)
                            ? 'border-[#C50410]/30 bg-[#FDF2F3]'
                            : 'border-transparent bg-white hover:border-gray-200'
                        )}
                      >
                        <span className='flex items-center gap-3'>
                          <Icon className='h-5 w-5 text-[#C50410]' />
                          {itemName}
                        </span>
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 text-gray-400 transition-transform',
                            isMenuExpanded(item.translationKey) && 'rotate-180'
                          )}
                        />
                      </button>

                      {item.subItems && isMenuExpanded(item.translationKey) && (
                        <div className='ml-8 space-y-1'>
                          {item.subItems.map(subItem => {
                            const SubIcon = subItem.icon
                            const subActive = isActive(subItem.href)
                            return (
                              <Link
                                key={subItem.href}
                                to={subItem.href}
                                className={cn(
                                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-gray-50 hover:text-gray-700',
                                  subActive
                                    ? 'text-[#181D27] font-medium bg-[#F7F8FA]'
                                    : 'text-[#667085]'
                                )}
                              >
                                {SubIcon && (
                                  <SubIcon
                                    className={cn(
                                      'h-4 w-4',
                                      subActive ? 'text-[#C50410]' : ''
                                    )}
                                  />
                                )}
                                <span className='flex-1'>
                                  {t(subItem.translationKey)}
                                </span>
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href!}
                      className={cn(
                        'flex items-center gap-3 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors hover:bg-gray-50 hover:text-gray-700',
                        isActive(item.href!)
                          ? 'border-[#C50410]/30 bg-[#FDF2F3] text-[#181D27]'
                          : 'border-transparent bg-white text-[#414651]'
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-5 w-5',
                          isActive(item.href!)
                            ? 'text-[#C50410]'
                            : 'text-[#667085]'
                        )}
                      />
                      <span className='flex-1'>{itemName}</span>
                    </Link>
                  )}
                </div>
              )
            })}
          </nav>
        </div>

        <div className='px-4 space-y-5'>
          <div className='space-y-2'>
            <div className='rounded-2xlp-3 space-y-2'>
              <div className='flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2'>
                <div className='flex items-center gap-2 text-sm font-semibold text-[#414651]'>
                  <Globe className='h-4 w-4' />
                  {t('common.language')}
                </div>
                <LanguageSwitcher>
                  <button className='flex items-center gap-1 rounded-lg bg-white px-2 py-1 text-sm font-medium text-[#414651] outline-none'>
                    <span className='inline-flex h-6 w-8 border border-gray-200 rounded-2xl text-xs items-center justify-center ml-2'>
                      {LANGUAGE[language]}
                    </span>
                    <ChevronsUpDown className='h-3 w-3 text-gray-400' />
                  </button>
                </LanguageSwitcher>
              </div>
            </div>
          </div>

          {user && (
            <section className='flex items-center gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3'>
              <div className='h-10 w-10 rounded-2xl bg-gray-100 flex items-center justify-center border border-gray-200'>
                <User className='h-5 w-5 text-gray-600' />
              </div>
              <p className='overflow-hidden flex-1 text-sm font-semibold text-gray-900 truncate'>
                {user.username}
              </p>
              <Button
                variant='ghost'
                size='icon'
                onClick={handleLogout}
                className='h-8 w-8 rounded-full hover:bg-gray-100'
                title={t('common.logout')}
              >
                <LogOut className='h-4 w-4 text-gray-500 hover:text-gray-700' />
              </Button>
            </section>
          )}
        </div>
      </aside>

      <main className='flex-1 flex flex-col min-w-0 overflow-x-hidden'>
        <section className='flex-1 overflow-y-auto overflow-x-hidden min-w-0'>
          <Outlet />
        </section>
      </main>
    </div>
  )
}
